import { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { BookOpen, AlertCircle, Search } from 'lucide-react';
import { apiFetch } from '../lib/supabase';
import BsddLookupWidget from '../components/BsddLookupWidget';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_COLOR = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  major: 'bg-orange-100 text-orange-800 border-orange-200',
  minor: 'bg-yellow-100 text-yellow-800 border-yellow-200',
};

export default function StandardsLibraryPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const resp = await apiFetch(`${API_BASE}/api/rules`);
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const json = await resp.json();
        if (!cancelled) {
          setRules(Array.isArray(json.rules) ? json.rules : []);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load rules');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const q = filter.trim().toLowerCase();
  const visible = !q
    ? rules
    : rules.filter(
        (r) =>
          (r.rule_id || '').toLowerCase().includes(q) ||
          (r.rule_name || '').toLowerCase().includes(q) ||
          (r.category || '').toLowerCase().includes(q) ||
          (r.description || '').toLowerCase().includes(q)
      );

  const grouped = visible.reduce((acc, r) => {
    const cat = r.category || 'OTHER';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(r);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="standards-library-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-primary" /> Standards Library
          </h1>
          <p className="text-muted-foreground">
            Project-agnostic Quality Gate rules. These ship with every audit and
            are the baseline before any project-specific BEP rules are layered on
            top.
          </p>
        </div>

        <Alert className="mb-6" data-testid="standards-library-info">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This is a read-only view of the catalogue. To toggle a rule on/off
            for the whole tenant, an admin role on the rules endpoint is
            required (P1 backlog). To override behaviour per project, upload
            and confirm a BEP on the project page.
          </AlertDescription>
        </Alert>

        {/* Sprint 5 — bSDD lookup widget */}
        <div className="mb-6">
          <BsddLookupWidget />
        </div>

        <div className="mb-4 relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by id, name, category, or description…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-9"
            data-testid="standards-search-input"
          />
        </div>

        {loading && (
          <p className="text-muted-foreground py-6" data-testid="standards-loading">
            Loading standards catalogue…
          </p>
        )}

        {error && (
          <Alert variant="destructive" data-testid="standards-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !error && rules.length === 0 && (
          <Card data-testid="standards-empty-state">
            <CardContent className="py-10 text-center text-muted-foreground">
              No standards have been seeded yet. Run{' '}
              <code className="px-1.5 py-0.5 rounded bg-muted text-foreground">
                POST /api/rules/seed
              </code>{' '}
              to populate the catalogue.
            </CardContent>
          </Card>
        )}

        {!loading && !error && visible.length > 0 && (
          <div className="space-y-6">
            {Object.entries(grouped).map(([cat, items]) => (
              <Card key={cat} data-testid={`standards-category-${cat}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{cat}</span>
                    <Badge variant="outline">{items.length}</Badge>
                  </CardTitle>
                  <CardDescription>
                    {items.length} rule{items.length === 1 ? '' : 's'} in this
                    category
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y divide-border">
                    {items.map((r) => (
                      <li
                        key={r.rule_id}
                        className="py-3 flex items-start justify-between gap-4"
                        data-testid={`standards-rule-${r.rule_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">
                            {r.rule_name || r.rule_id}
                          </p>
                          {r.description && (
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {r.description}
                            </p>
                          )}
                          <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground/70">
                            id: {r.rule_id}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {r.severity && (
                            <span
                              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
                                SEVERITY_COLOR[r.severity] ||
                                'bg-muted text-foreground border-border'
                              }`}
                            >
                              {r.severity}
                            </span>
                          )}
                          <span
                            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
                              r.is_active
                                ? 'bg-green-100 text-green-800 border-green-200'
                                : 'bg-muted text-muted-foreground border-border'
                            }`}
                          >
                            {r.is_active ? 'active' : 'inactive'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
